import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  FileText,
  FolderKanban,
  MapPin,
  Network,
  Pencil,
  UploadCloud,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SOVEREIGN } from '../../../utils/epcProjectVisual';
import LocationMiniMap, { type MapPoint } from '../components/LocationMiniMap';
import TimelineGraph from '../components/TimelineGraph';
import { mdy } from '../format';
import type { TabId } from '../progress';
import type { LocationOption } from '../types';
import { BanknoteRule, HelperText } from '../ui';

export type CreatedProject = { id: string; code: string; name: string };

function ReviewCard({ title, icon: Icon, editTab, onJump, children }: {
  title: string; icon: LucideIcon; editTab: TabId; onJump: (id: TabId) => void; children: ReactNode;
}) {
  return (
    <section className="relative rounded-2xl p-4" style={{ border: '1px solid rgba(11,59,46,0.35)', background: 'rgba(250,247,238,0.96)' }}>
      <div className="mb-3 flex items-center justify-between">
        <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: SOVEREIGN.title }}>
          <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
          {title}
        </p>
        <button
          type="button"
          onClick={() => onJump(editTab)}
          className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-bold transition hover:opacity-80"
          style={{ background: 'rgba(51,146,116,0.12)', color: '#1F5A47' }}
        >
          <Pencil className="h-2.5 w-2.5" />
          Edit
        </button>
      </div>
      {children}
    </section>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <p className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: SOVEREIGN.kpiCaption }}>{label}</p>
      <p className={`truncate text-sm font-bold ${mono ? 'font-mono tabular-nums' : ''}`} style={{ color: SOVEREIGN.title }}>{value || '—'}</p>
    </div>
  );
}

function NextActionCard({ icon: Icon, title, caption, onClick }: { icon: LucideIcon; title: string; caption: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative flex flex-col items-start gap-2 rounded-2xl p-4 text-left transition hover:-translate-y-0.5"
      style={{ border: '1px solid rgba(11,59,46,0.35)', background: 'rgba(250,247,238,0.96)', boxShadow: '0 1px 2px rgba(11,59,46,0.04)' }}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${SOVEREIGN.bottle}, ${SOVEREIGN.title})` }}>
        <Icon className="h-5 w-5" strokeWidth={2} />
      </span>
      <span className="text-sm font-extrabold" style={{ color: SOVEREIGN.title }}>{title}</span>
      <span className="text-[11px] leading-snug" style={{ color: SOVEREIGN.kpiCaption }}>{caption}</span>
      <span className="mt-auto inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: '#8A5A08' }}>
        Continue <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}

export default function OverviewStep(props: {
  mode: 'create' | 'edit';
  created: CreatedProject | null;
  onJump: (id: TabId) => void;
  missing: string[];
  code: string;
  name: string;
  typeLabel: string;
  customerLabel: string;
  statusLabel: string;
  locations: LocationOption[];
  showDcMw: boolean;
  showAcMw: boolean;
  dcMw?: number;
  acMw?: number;
  startDate: string;
  endDatePlanned: string;
  commissioningDate: string;
  accent: string;
  docNames: string[];
  remarks: string;
}) {
  const navigate = useNavigate();
  const {
    mode, created, onJump, missing, code, name, typeLabel, customerLabel, statusLabel,
    locations, showDcMw, showAcMw, dcMw, acMw, startDate, endDatePlanned, commissioningDate,
    accent, docNames, remarks,
  } = props;

  /* ---------- Success state (create mode, after 2xx) ---------- */
  if (created) {
    return (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="relative py-4">
        <div className="relative mx-auto max-w-2xl rounded-2xl p-8 text-center" style={{ border: '1px solid rgba(11,59,46,0.35)', background: 'rgba(250,247,238,0.96)' }}>
          <BanknoteRule />
          <motion.span
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.1 }}
            className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
            style={{ background: 'rgba(201,169,97,0.18)', border: '2px solid #C9A961' }}
          >
            <CheckCircle2 className="h-8 w-8" style={{ color: SOVEREIGN.bottle }} strokeWidth={2.2} />
          </motion.span>
          <p className="font-mono text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: '#8A5A08' }}>{created.code}</p>
          <h2 className="mt-1 text-xl font-extrabold tracking-tight" style={{ color: SOVEREIGN.title }}>
            {created.name}
          </h2>
          <p className="mt-1 text-sm font-medium" style={{ color: SOVEREIGN.brand }}>Project created successfully</p>
          <p className="mt-4 text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: SOVEREIGN.kpiCaption }}>What next?</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <NextActionCard
              icon={UploadCloud}
              title="Import MPP schedule"
              caption="Bring in the project plan from Microsoft Project"
              onClick={() => navigate(`/epc/projects/${created.id}/schedule`)}
            />
            <NextActionCard
              icon={Network}
              title="Build team hierarchy"
              caption="Set up departments, heads, managers & engineers"
              onClick={() => navigate(`/epc/projects/${created.id}/team`)}
            />
            <NextActionCard
              icon={FolderKanban}
              title="Close"
              caption="Go to the project overview"
              onClick={() => navigate(`/epc/projects/${created.id}/overview`)}
            />
          </div>
        </div>
      </motion.div>
    );
  }

  /* ---------- Review state ---------- */
  const points: MapPoint[] = locations
    .filter((l): l is LocationOption & { lat: number; lng: number } => l.lat != null && l.lng != null)
    .map((l) => ({ id: l.value, name: l.name, lat: l.lat, lng: l.lng, primary: locations[0]?.value === l.value }));

  return (
    <div className="space-y-4">
      {missing.length > 0 ? (
        <div className="rounded-2xl p-4" style={{ border: '1px solid rgba(201,169,97,0.5)', background: 'rgba(201,169,97,0.10)' }}>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: '#8A5A08' }}>
            Complete before {mode === 'edit' ? 'saving' : 'creating'}
          </p>
          <ul className="mt-1.5 list-inside list-disc text-sm font-medium" style={{ color: '#8A5A08' }}>
            {missing.map((m) => <li key={m}>{m}</li>)}
          </ul>
        </div>
      ) : (
        <HelperText tone="info">Everything checks out — review the summary and hit {mode === 'edit' ? '"Save changes"' : '"Create project"'} below.</HelperText>
      )}

      <ReviewCard title="Project basics" icon={FolderKanban} editTab="basics" onJump={onJump}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Fact label="Code" value={code} mono />
          <Fact label="Type" value={typeLabel} />
          <Fact label="Customer" value={customerLabel} />
          <Fact label="Status" value={statusLabel} />
        </div>
        <p className="mt-3 text-base font-extrabold tracking-tight" style={{ color: SOVEREIGN.title }}>{name || 'Untitled project'}</p>
      </ReviewCard>

      <ReviewCard title={`Site locations (${locations.length})`} icon={MapPin} editTab="location" onJump={onJump}>
        {locations.length ? (
          <div className="grid gap-3 lg:grid-cols-2">
            <ul className="space-y-1 text-sm font-medium" style={{ color: SOVEREIGN.ink }}>
              {locations.map((l, i) => (
                <li key={l.value} className="flex items-center gap-1.5">
                  <span style={{ color: '#8A5A08' }}>{i === 0 ? '★' : '·'}</span>
                  <span className="truncate">{l.name}{l.state ? ` — ${l.state}` : ''}</span>
                </li>
              ))}
            </ul>
            {points.length ? <LocationMiniMap points={points} height={180} /> : null}
          </div>
        ) : (
          <p className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>No sites selected.</p>
        )}
      </ReviewCard>

      <ReviewCard title="Capacity & timeline" icon={Zap} editTab="timeline" onJump={onJump}>
        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {showDcMw ? <Fact label="DC capacity" value={dcMw != null ? `${dcMw} MW` : '—'} mono /> : null}
          {showAcMw ? <Fact label="AC capacity" value={acMw != null ? `${acMw} MW` : '—'} mono /> : null}
          <Fact label="Start" value={mdy(startDate)} mono />
          <Fact label="Planned end" value={mdy(endDatePlanned)} mono />
        </div>
        <TimelineGraph start={startDate} end={endDatePlanned} commissioning={commissioningDate} accent={accent} />
      </ReviewCard>

      <ReviewCard title={`Documents & notes (${docNames.length})`} icon={FileText} editTab="docs" onJump={onJump}>
        {docNames.length ? (
          <ul className="grid gap-1 text-sm font-medium sm:grid-cols-2" style={{ color: SOVEREIGN.ink }}>
            {docNames.map((d) => <li key={d} className="truncate">📎 {d}</li>)}
          </ul>
        ) : (
          <p className="text-sm" style={{ color: SOVEREIGN.kpiCaption }}>No documents attached.</p>
        )}
        {remarks.trim() ? <p className="mt-2 rounded-lg p-2 text-xs" style={{ background: 'rgba(11,59,46,0.04)', color: SOVEREIGN.ink }}>{remarks}</p> : null}
      </ReviewCard>

      <div className="flex items-center gap-2 text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
        <Building2 className="h-3.5 w-3.5" />
        Use the {mode === 'edit' ? 'Save changes' : 'Create project'} button below to finish.
      </div>
    </div>
  );
}
